import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import GrowthDashboard from "@/components/GrowthDashboard";
import KpiMatrixPanel from "@/components/kpi/KpiMatrixPanel";

const STATUS_CHIP = {
  expired: "bg-red-600 text-white",
  expiring_30: "bg-amber-500 text-white",
  expiring_60: "bg-amber-400 text-slate-900",
  expiring_90: "bg-yellow-300 text-slate-900",
  valid: "bg-teal-600 text-white",
};
const STATUS_LABEL = {
  expired: "EXPIRED",
  expiring_30: "≤ 30 DAYS",
  expiring_60: "≤ 60 DAYS",
  expiring_90: "≤ 90 DAYS",
  valid: "VALID",
};

const InfoRow = ({ label, value, testId }) => (
  <div className="flex items-center px-4 py-2.5 bg-white">
    <span className="w-44 shrink-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
    <span className="text-[13px] text-slate-700" data-testid={testId}>{value ?? "—"}</span>
  </div>
);

const NationalityRow = ({ emp, canEdit, onSaved }) => {
  const save = async (value) => {
    try {
      await api.patch(`/employees/${emp.id}`, { nationality: value });
      toast.success(`Nationality set to ${value} — pension eligibility recomputed on next payroll run`);
      onSaved(value);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center px-4 py-2.5 bg-white">
      <span className="w-44 shrink-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Nationality</span>
      {canEdit ? (
        <div className="flex items-center gap-3">
          <Select value={emp.nationality || ""} onValueChange={save}>
            <SelectTrigger className="rounded-none w-32 h-8 text-[13px]" data-testid="nationality-select">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="Qatari" className="text-[13px]">Qatari</SelectItem>
              <SelectItem value="Expat" className="text-[13px]">Expat</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[11px] text-muted-foreground">
            Seed value inferred from surname (demo heuristic) — please verify. Drives pension eligibility.
          </span>
        </div>
      ) : (
        <span className="text-[13px] text-slate-700" data-testid="profile-nationality">{emp.nationality ?? "—"}</span>
      )}
    </div>
  );
};

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setEmp(null);
    setError("");
    api.get(`/employees/${id}`)
      .then((r) => setEmp(r.data))
      .catch((e) => setError(e.response?.status === 403 ? "You do not have access to this employee's record." : "Employee not found."));
    api.get("/documents", { params: { employee_id: id } })
      .then((r) => setDocs(r.data.documents))
      .catch(() => {});
  }, [id]);

  if (error) {
    return (
      <div className="border border-border bg-white p-10 text-center" data-testid="employee-access-denied">
        <Lock className="h-6 w-6 mx-auto text-muted-foreground mb-3" />
        <p className="text-[14px] text-slate-700">{error}</p>
      </div>
    );
  }
  if (!emp) return <Skeleton className="h-96 w-full rounded-none" />;

  const hasComp = emp.salary_qar != null;

  return (
    <div className="space-y-6 fade-in" data-testid="employee-detail-page">
      <Button variant="ghost" onClick={() => navigate(-1)} data-testid="employee-back-btn"
        className="rounded-none h-8 text-[12px] text-muted-foreground -ml-2">
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>

      <div className="bg-white border border-border p-6 flex items-start gap-5">
        <div className="h-16 w-16 bg-[var(--brand-primary)] text-white flex items-center justify-center text-xl font-bold shrink-0">
          {emp.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-bold text-[var(--brand-primary)]" data-testid="employee-detail-name">{emp.name}</h2>
            <Badge variant="outline" className="rounded-none text-[10px] font-mono border-slate-300 text-slate-600">{emp.id}</Badge>
            {emp.is_spof && (
              <Badge className="rounded-none text-[10px] font-mono bg-red-600 hover:bg-red-600 text-white gap-1">
                <AlertTriangle className="h-3 w-3" /> SINGLE POINT OF FAILURE
              </Badge>
            )}
          </div>
          <p className="text-[13px] text-muted-foreground mt-0.5">{emp.title} · {emp.department} · Level {emp.level}</p>
          {emp.manager && (
            <p className="text-[12px] text-slate-600 mt-2">
              Reports to{" "}
              <button className="text-teal-700 font-medium hover:underline" onClick={() => navigate(`/employees/${emp.manager.id}`)}>
                {emp.manager.name}
              </button>{" "}
              ({emp.manager.title})
            </p>
          )}
        </div>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="rounded-none bg-white border border-border h-10 p-0">
          {["profile", "skills", "growth", "kpi matrix", "compensation", "documents"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t.replace(" ", "-")}`}
              className="rounded-none h-full px-5 text-[12px] uppercase tracking-wider data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <div className="border border-border divide-y divide-border" data-testid="profile-tab-content">
            <InfoRow label="Email" value={emp.email} testId="profile-email" />
            <InfoRow label="Phone" value={emp.phone} testId="profile-phone" />
            <InfoRow label="Join Date" value={emp.join_date} testId="profile-join-date" />
            {emp.qid && <InfoRow label="QID" value={emp.qid} testId="profile-qid" />}
            {emp.dob && <InfoRow label="Date of Birth" value={emp.dob} testId="profile-dob" />}
            <NationalityRow emp={emp} canEdit={["admin", "hr_manager"].includes(user.role)}
              onSaved={(n) => setEmp({ ...emp, nationality: n })} />
            <InfoRow label="KPI Overall" value={emp.kpi_scores?.overall} testId="profile-kpi" />
            <div className="flex items-center px-4 py-2.5 bg-white">
              <span className="w-44 shrink-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Attrition Factors</span>
              <div className="flex gap-1.5 flex-wrap">
                {(emp.attrition_risk_factors || []).length === 0 && <span className="text-[13px] text-slate-500">None flagged</span>}
                {(emp.attrition_risk_factors || []).map((f) => (
                  <span key={f} className="text-[10px] font-mono bg-amber-50 border border-amber-300 text-amber-800 px-1.5 py-0.5">{f}</span>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="skills" className="mt-4">
          <div className="bg-white border border-border p-5 space-y-3" data-testid="skills-tab-content">
            {(emp.skills || []).map((s) => (
              <div key={s.name} className="flex items-center gap-3">
                <span className="w-52 text-[12px] text-slate-600 shrink-0">{s.name}</span>
                <span className="text-[10px] font-mono text-slate-400 w-20 shrink-0">{s.category}</span>
                <div className="flex-1 h-2 bg-slate-100">
                  <div className="h-2 bg-teal-600" style={{ width: `${(s.proficiency / 5) * 100}%` }} />
                </div>
                <span className="text-[11px] font-mono text-slate-500">{s.proficiency}/5</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="growth" className="mt-4">
          <GrowthDashboard employeeId={id} showHeader={false} />
        </TabsContent>

        <TabsContent value="kpi matrix" className="mt-4">
          <KpiMatrixPanel employeeId={id} />
        </TabsContent>

        <TabsContent value="compensation" className="mt-4">
          {hasComp ? (
            <div className="border border-border divide-y divide-border" data-testid="compensation-tab-content">
              <InfoRow label="Base Salary" value={`QAR ${emp.salary_qar.toLocaleString()} / month`} testId="comp-salary" />
              <InfoRow label="Allowances" value={`QAR ${(emp.allowances_qar || 0).toLocaleString()} / month`} testId="comp-allowances" />
              <InfoRow label="Total Monthly Cost" value={`QAR ${(emp.salary_qar + (emp.allowances_qar || 0)).toLocaleString()}`} testId="comp-total" />
            </div>
          ) : (
            <div className="border border-border bg-white p-10 text-center" data-testid="compensation-restricted">
              <Lock className="h-6 w-6 mx-auto text-muted-foreground mb-3" />
              <p className="text-[14px] text-slate-700">Compensation data restricted</p>
              <p className="text-[12px] text-muted-foreground mt-1">Visible to Administrators and HR Managers only.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="bg-white border border-border divide-y divide-border" data-testid="documents-tab-content">
            {docs.length === 0 && <p className="text-[13px] text-muted-foreground text-center py-8">No documents on file</p>}
            {docs.map((d) => (
              <div key={d.id} className="flex items-center px-4 py-3 gap-4" data-testid={`doc-row-${d.id}`}>
                <span className="w-40 text-[12px] uppercase tracking-wider text-muted-foreground shrink-0">{d.doc_type}</span>
                <div className="flex-1">
                  <p className="text-[13px] font-medium text-slate-800">{d.title}</p>
                  <p className="text-[11px] font-mono text-muted-foreground">No. {d.doc_number} · issued {d.issue_date}</p>
                </div>
                <span className="text-[12px] font-mono text-slate-600">exp {d.expiry_date}</span>
                <span className={`text-[10px] font-mono px-2 py-1 ${STATUS_CHIP[d.status]}`}>{STATUS_LABEL[d.status]}</span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
